import { Flex, Spinner, Text } from "@chakra-ui/react";
import moment from "moment";
import React, { useContext, useState } from "react";
import { FormattedMessage } from "react-intl";
import { useSelector } from "react-redux";

import { LocaleContext } from "@nens/react/contexts";
import { useOnlyOnce } from "@nens/react/hooks";
import { getUuidFromUrl } from "@nens/shared-utils";

import { getV3Api } from "../apiConfiguration";
import { getSimulation, getThreediModel } from "../state/simulation";

import { FormLabel } from "./FormLabel";
import LeftMenuHeader from "./LeftMenuHeader";

type SimulationInfoType = {
  id: string;
  formattedMessage: JSX.Element;
  value?: string | number | JSX.Element;
};

const InfoMenu = () => {
  const simulation = useSelector(getSimulation);
  const model = useSelector(getThreediModel);
  const clonedFromId = simulation.clonedFrom && getUuidFromUrl(simulation.clonedFrom);
  const [templateName, setTemplatename] = useState<string | undefined>(undefined);
  const v3Api = getV3Api();
  const { locale } = useContext(LocaleContext);
  moment.locale(locale);

  useOnlyOnce(() => {
    const getTemplates = async () => {
      if (!model || model.id === undefined) return;
      const templates = await v3Api.simulationTemplatesList({
        simulationThreedimodelId: model.id,
      });
      const template = templates.results.find(
        (result) => result.simulation && clonedFromId && result.simulation.id === +clonedFromId
      );
      setTemplatename(template ? template.name : "Unknown");
    };
    getTemplates();
  });

  const simulationInfo: SimulationInfoType[] = [
    {
      id: "template_name",
      formattedMessage: (
        <FormattedMessage id="infoMenu.template_name" defaultMessage="Template name" />
      ),
      value: templateName || <Spinner size="xl" w={10} h={10} speed="1s" />,
    },
    {
      id: "simulation_name",
      formattedMessage: (
        <FormattedMessage id="infoMenu.simulation_name" defaultMessage="Simulation name" />
      ),
      value: simulation.name,
    },
    {
      id: "simulation_id",
      formattedMessage: (
        <FormattedMessage id="infoMenu.simulation_id" defaultMessage="Simulation id" />
      ),
      value: simulation.id,
    },
    {
      id: "simulation_description",
      formattedMessage: <FormattedMessage id="infoMenu.simulation_start" defaultMessage="Start" />,
      value: moment(simulation.startDatetime).format("L HH:mm"),
    },
    {
      id: "simulation_end",
      formattedMessage: <FormattedMessage id="infoMenu.simulation_end" defaultMessage="End" />,
      value: moment(simulation.endDatetime).format("L HH:mm"),
    },
    {
      id: "schematisation_name",
      formattedMessage: (
        <FormattedMessage id="infoMenu.schematisation_name" defaultMessage="Schematisation name" />
      ),
      value: model?.schematisationName ?? undefined,
    },
    {
      id: "schematisation_id",
      formattedMessage: (
        <FormattedMessage id="infoMenu.schematisation_id" defaultMessage="Schematisation id" />
      ),
      value: model?.schematisationId ?? undefined,
    },
    {
      id: "revision_number",
      formattedMessage: (
        <FormattedMessage id="infoMenu.revision_number" defaultMessage="Revision number" />
      ),
      value: model?.revisionNumber ?? undefined,
    },
    {
      id: "model_name",
      formattedMessage: <FormattedMessage id="infoMenu.model_name" defaultMessage="Model name" />,
      value: model ? model.name : undefined,
    },
    {
      id: "model_id",
      formattedMessage: <FormattedMessage id="infoMenu.model_id" defaultMessage="Model id" />,
      value: model ? model.id : undefined,
    },
    {
      id: "organisation",
      formattedMessage: (
        <FormattedMessage id="infoMenu.organisation" defaultMessage="Organisation" />
      ),
      value: simulation.organisationName,
    },
  ];

  return (
    <>
      <LeftMenuHeader>
        <FormattedMessage id="infoMenu.header" defaultMessage="Information" />
      </LeftMenuHeader>
      <Flex flexDirection="column" m="0 4px" justifyContent="start">
        {simulationInfo.map((info) => (
          <React.Fragment key={info.id}>
            <FormLabel w="100%">{info.formattedMessage}</FormLabel>
            <Text w="100%">{info.value}</Text>
          </React.Fragment>
        ))}
      </Flex>
    </>
  );
};

export default InfoMenu;
